import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { apiClient, PLATFORM_API_BASE_URL } from '@/lib/apiClient';

/**
 * P6-3 / M10 — platform-admin notification stream hook.
 *
 * Connects to /api/v1/platform/notifications/stream (SSE — wired in
 * P6-2) and maintains an in-memory list of notifications. The stream
 * sends three event types:
 *   - `replay`       initial last-24h snapshot on connect (D-P6-007)
 *   - `notification` new push (P6-1 emits via the in-process bus)
 *   - `agent_no_stream` agent identities (no per-user binding)
 *
 * The hook auto-reconnects with capped exponential backoff (1s, 2s,
 * 4s, ... up to 30s) so a Render reverse-proxy hiccup or a laptop
 * lid-close doesn't permanently kill the stream.
 *
 * EventSource doesn't support custom headers, so we never put the
 * bearer token in the URL. Instead we mint a single-use, ~60s ticket
 * via POST /notifications/stream-ticket (apiClient attaches the
 * Authorization header) and open the stream with `?ticket=`. A fresh
 * ticket is fetched on every (re)connect because tickets are
 * single-use and short-lived. This keeps the access token out of
 * access logs, browser history, and referrer headers (CLAUDE.md S3).
 *
 * markRead / markAllRead are best-effort REST writes — local state
 * flips immediately for snappy UX; a failed write logs but doesn't
 * roll back since the next stream replay corrects drift.
 */

/**
 * Closed set of notification types the platform stream can emit.
 *
 * The `chat.*` types (Platform Chat, docs/platform-chat/CONTRACTS.md §4) are
 * realtime signals, not inbox items: they are fanned out to
 * `subscribeToNotificationStream` listeners (the chat page) and kept out of
 * the bell list, so the inbox `NotificationDto['type']` excludes them.
 */
export type ChatNotificationType = 'chat.message' | 'chat.status' | 'chat.bridge';
export type NotificationType =
    | 'broadcast'
    | 'ticket_assigned'
    | 'ticket_replied'
    | 'sla_warning'
    | 'sla_breach'
    | 'system'
    | 'agent_force_approval'
    | 'agent_force_handoff'
    | 'agent_force_job_result'
    | ChatNotificationType;

export const CHAT_NOTIFICATION_TYPES: readonly ChatNotificationType[] = ['chat.message', 'chat.status', 'chat.bridge'];

export function isChatNotificationType(type: unknown): type is ChatNotificationType {
    return typeof type === 'string' && (CHAT_NOTIFICATION_TYPES as readonly string[]).includes(type);
}

/** A notification exactly as it arrives on the stream (any type). */
export interface StreamNotificationDto extends Omit<NotificationDto, 'type'> {
    type: NotificationType;
}

export type StreamNotificationListener = (notification: StreamNotificationDto) => void;

const streamListeners = new Set<StreamNotificationListener>();

/**
 * Listen to live `notification` pushes from the stream opened by
 * useNotificationStream (mounted once by the TopBar bell) without opening a
 * second EventSource. Replay snapshots are not re-dispatched. Returns an
 * unsubscribe function.
 */
export function subscribeToNotificationStream(listener: StreamNotificationListener): () => void {
    streamListeners.add(listener);
    return () => {
        streamListeners.delete(listener);
    };
}

/** Fans one live stream notification out to every listener (exported for tests). */
export function dispatchStreamNotification(notification: StreamNotificationDto): void {
    for (const listener of [...streamListeners]) {
        try {
            listener(notification);
        } catch {
            /* a faulty listener must not break the stream or other listeners */
        }
    }
}

export interface NotificationDto {
    id: string;
    recipientUserId: string;
    /** Inbox notification types; chat.* realtime signals never reach the list. */
    type: Exclude<NotificationType, ChatNotificationType>;
    priority: 'low' | 'normal' | 'high' | 'critical';
    title: string;
    body: string | null;
    payload: Record<string, unknown>;
    actionUrl: string | null;
    readAt: string | null;
    dismissedAt: string | null;
    expiresAt: string | null;
    createdAt: string;
}

export interface UseNotificationStreamResult {
    notifications: NotificationDto[];
    unreadCount: number;
    isConnected: boolean;
    markRead: (id: string) => Promise<void>;
    markAllRead: () => Promise<void>;
}

const MAX_LIST_LEN = 100;
const MAX_BACKOFF_MS = 30_000;
const BASE_BACKOFF_MS = 1_000;

function isInboxNotification(n: StreamNotificationDto | NotificationDto): n is NotificationDto {
    return !isChatNotificationType(n.type);
}

function dedupeById(items: Array<StreamNotificationDto | NotificationDto>): NotificationDto[] {
    const seen = new Set<string>();
    const out: NotificationDto[] = [];
    for (const n of items) {
        if (!isInboxNotification(n)) continue;
        if (seen.has(n.id)) continue;
        seen.add(n.id);
        out.push(n);
    }
    return out;
}

export function useNotificationStream(): UseNotificationStreamResult {
    const accessToken = useAuthStore((s) => s.accessToken);
    const [notifications, setNotifications] = useState<NotificationDto[]>([]);
    const [isConnected, setIsConnected] = useState(false);
    const sourceRef = useRef<EventSource | null>(null);
    const backoffRef = useRef(BASE_BACKOFF_MS);
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const upsertOne = useCallback((incoming: StreamNotificationDto) => {
        setNotifications((prev) =>
            dedupeById([incoming, ...prev]).slice(0, MAX_LIST_LEN),
        );
    }, []);

    const replaceAll = useCallback((incoming: StreamNotificationDto[]) => {
        setNotifications(dedupeById(incoming).slice(0, MAX_LIST_LEN));
    }, []);

    useEffect(() => {
        if (!accessToken) return;

        let cancelled = false;

        const scheduleReconnect = (): void => {
            if (cancelled) return;
            const wait = Math.min(backoffRef.current, MAX_BACKOFF_MS);
            backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);
            reconnectTimerRef.current = setTimeout(() => {
                void connect();
            }, wait);
        };

        const connect = async (): Promise<void> => {
            if (cancelled) return;

            // Mint a fresh single-use ticket first; apiClient attaches the
            // bearer header and transparently refreshes on 401. A failure
            // here (network blip, or refresh exhausted) is treated like a
            // stream drop: back off and retry.
            let ticket: string;
            try {
                const res = await apiClient.post<{ data: { ticket: string } }>(
                    '/notifications/stream-ticket',
                );
                ticket = res.data?.data?.ticket ?? '';
            } catch {
                scheduleReconnect();
                return;
            }
            if (cancelled) return;
            if (!ticket) {
                scheduleReconnect();
                return;
            }

            const url = `${PLATFORM_API_BASE_URL}/notifications/stream?ticket=${encodeURIComponent(ticket)}`;
            const es = new EventSource(url, { withCredentials: true });
            sourceRef.current = es;

            es.addEventListener('open', () => {
                setIsConnected(true);
                backoffRef.current = BASE_BACKOFF_MS;
            });

            es.addEventListener('replay', (ev) => {
                try {
                    const payload = JSON.parse((ev as MessageEvent).data) as {
                        notifications: StreamNotificationDto[];
                    };
                    replaceAll(payload.notifications ?? []);
                } catch {
                    /* swallow parse errors — next replay will correct */
                }
            });

            es.addEventListener('notification', (ev) => {
                try {
                    const payload = JSON.parse((ev as MessageEvent).data) as StreamNotificationDto;
                    dispatchStreamNotification(payload);
                    upsertOne(payload);
                } catch {
                    /* ignore */
                }
            });

            es.addEventListener('agent_no_stream', () => {
                // Agent identity — server closes after this event.
                setIsConnected(false);
            });

            es.addEventListener('error', () => {
                setIsConnected(false);
                es.close();
                sourceRef.current = null;
                // Re-fetch a fresh ticket on the next attempt (the previous
                // one was single-use and is now spent).
                scheduleReconnect();
            });
        };

        void connect();

        return (): void => {
            cancelled = true;
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            sourceRef.current?.close();
            sourceRef.current = null;
            setIsConnected(false);
        };
    }, [accessToken, replaceAll, upsertOne]);

    const markRead = useCallback(async (id: string): Promise<void> => {
        setNotifications((prev) =>
            prev.map((n) => (n.id === id && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n)),
        );
        try {
            await apiClient.patch(`/notifications/${id}/read`);
        } catch {
            // Local state stays optimistic; the next replay corrects drift.
        }
    }, []);

    const markAllRead = useCallback(async (): Promise<void> => {
        const stamp = new Date().toISOString();
        setNotifications((prev) =>
            prev.map((n) => (n.readAt ? n : { ...n, readAt: stamp })),
        );
        try {
            await apiClient.patch('/notifications/read-all');
        } catch {
            /* swallow */
        }
    }, []);

    const unreadCount = notifications.filter((n) => !n.readAt && !n.dismissedAt).length;

    return { notifications, unreadCount, isConnected, markRead, markAllRead };
}
